// ./src/components/features/EmailLayouts/EmailLayoutsPreviewModal/EmailLayoutsPreviewModal.tsx
import React, { useState, useEffect } from "react";
import { Stack, Text, Group, Badge, Skeleton, Box, Alert } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { EmailLayout, isSystemLayout } from "@/utils/api";
import { AppDrawer, StatusBadge } from "@/components/generic";

export interface EmailLayoutsPreviewModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Email layout to preview */
  emailLayout?: EmailLayout | null;

  /** Callback to fetch preview with rendered content */
  onPreviewEmailLayout?: (
    id: string,
    sampleData?: string
  ) => Promise<EmailLayout | null>;

  /** Whether preview operation is in progress */
  isPreviewing?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

export const EmailLayoutsPreviewModal: React.FC<
  EmailLayoutsPreviewModalProps
> = ({
  opened,
  onClose,
  emailLayout,
  onPreviewEmailLayout,
  isPreviewing = false,
  lastError,
  onClearError,
}) => {
  const [previewData, setPreviewData] = useState<EmailLayout | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const isSystemLayoutItem = emailLayout ? isSystemLayout(emailLayout) : false;

  // Fetch preview when modal opens
  useEffect(() => {
    if (opened && emailLayout?.id && onPreviewEmailLayout) {
      const fetchPreview = async () => {
        setIsLoadingPreview(true);
        setPreviewData(null);
        onClearError?.();

        try {
          const result = await onPreviewEmailLayout(emailLayout.id!);
          setPreviewData(result);
        } catch (error) {
          console.error("Failed to fetch preview:", error);
        } finally {
          setIsLoadingPreview(false);
        }
      };

      fetchPreview();
    }

    // Reset state when modal closes
    if (!opened) {
      setPreviewData(null);
      setIsLoadingPreview(false);
    }
  }, [opened, emailLayout?.id, onPreviewEmailLayout, onClearError]);

  const renderLoadingSkeleton = () => (
    <Stack gap="md">
      {/* Content skeleton */}
      <Box
        style={{
          border: "1px solid var(--mantine-color-gray-3)",
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: "#fff",
        }}
      >
        <Skeleton height={32} width="60%" mb={16} />
        <Skeleton height={16} width="90%" mb={8} />
        <Skeleton height={16} width="85%" mb={8} />
        <Skeleton height={16} width="75%" mb={16} />
        <Skeleton height={24} width="40%" mb={12} />
        <Skeleton height={16} width="100%" mb={8} />
        <Skeleton height={16} width="95%" mb={8} />
        <Skeleton height={16} width="88%" mb={8} />
        <Skeleton height={16} width="92%" mb={16} />
        <Skeleton height={14} width="50%" mb={4} />
        <Skeleton height={14} width="45%" />
      </Box>
    </Stack>
  );

  const renderPreviewContent = () => {
    if (isLoadingPreview || isPreviewing) {
      return renderLoadingSkeleton();
    }

    if (lastError) {
      return (
        <Alert icon={<IconInfoCircle size={16} />} color="red" variant="light">
          {lastError}
        </Alert>
      );
    }

    if (!previewData) {
      return (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
          No preview available. The layout may not have any content yet.
        </Alert>
      );
    }

    return (
      <Stack gap="md">
        {/* HTML content preview */}
        {previewData.htmlContent && (
          <Box>
            <Text size="sm" fw={500} c="dimmed" mb={8}>
              Layout Preview
            </Text>
            <Box
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "8px",
                padding: "16px",
                backgroundColor: "#fff",
                maxHeight: "500px",
                overflow: "auto",
              }}
              dangerouslySetInnerHTML={{ __html: previewData.htmlContent }}
            />
          </Box>
        )}

        {/* Plain text preview (if no HTML) */}
        {!previewData.htmlContent && previewData.textContent && (
          <Box>
            <Text size="sm" fw={500} c="dimmed" mb={8}>
              Layout Preview (Plain Text)
            </Text>
            <Box
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "8px",
                padding: "16px",
                backgroundColor: "#fff",
                fontFamily: "monospace",
                fontSize: "13px",
                whiteSpace: "pre-wrap",
              }}
            >
              {previewData.textContent}
            </Box>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title="Email Layout Preview"
      position="right"
      size="lg"
      closable={!isLoadingPreview}
      actionType="close"
      errors={lastError}
    >
      <Stack gap="md">
        {/* Layout info header */}
        {emailLayout && (
          <Box
            style={{
              padding: "12px 16px",
              backgroundColor: "var(--mantine-color-gray-0)",
              borderRadius: "8px",
            }}
          >
            <Group gap="xs" mb={4}>
              <Text fw={600} size="md">
                {emailLayout.name || "Untitled Layout"}
              </Text>
              {isSystemLayoutItem && (
                <Badge variant="light" color="blue" size="xs">
                  System
                </Badge>
              )}
            </Group>
            <Group gap="md">
              {emailLayout.status !== undefined && (
                <Group gap={4}>
                  <Text size="sm" c="dimmed">
                    Status:
                  </Text>
                  <StatusBadge status={emailLayout.status} size="xs" />
                </Group>
              )}
            </Group>
            {emailLayout.description && (
              <Text size="sm" c="dimmed" mt={8}>
                {emailLayout.description}
              </Text>
            )}
          </Box>
        )}

        {/* Info about preview */}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          This preview shows how the layout will appear with sample content. The
          actual layout will wrap around your email template content.
        </Alert>

        {/* Preview content */}
        {renderPreviewContent()}
      </Stack>
    </AppDrawer>
  );
};
